import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSessionUser, getUserByPhone, saveSessionUser } from '../services/userService';
import { useSeo } from '../hooks/useSeo';
import '../cms.css';
import { UserProfile, Expert, MoonPhaseRow } from '../types';
import { 
    Loader2, Trash2, Edit2, X, Sliders, Settings, 
    Copy, Check, TestTube, RefreshCw, Users, Briefcase, Bot, MessageSquare
} from 'lucide-react';

import UsersTab from './CMS/UsersTab';
import ExpertsTab from './CMS/ExpertsTab';
import OrdersTab from './CMS/OrdersTab';
import ProductsTab from './CMS/ProductsTab';
import HoroscopeTab from './CMS/HoroscopeTab';
import BlogTab from './CMS/BlogTab';
import MediaTab from './CMS/MediaTab';
import PaymentsTab from './CMS/PaymentsTab';
import TestingTab from './CMS/TestingTab';
import StatsTab from './CMS/StatsTab';
import SettingsPage from './CMS/SettingsPage';
import YearlyTab from './CMS/Horoscope/YearlyTab';
import AutoUpdaterTab from './CMS/Horoscope/AutoUpdaterTab';
import MonthlyTab from './CMS/Horoscope/MonthlyTab';
import WeeklyTab from './CMS/Horoscope/WeeklyTab';
import DailyTab from './CMS/Horoscope/DailyTab';
import CommentsManager from './CMS/Blog/CommentsManager';
import CmsChatPage from './CMS/CmsChatPage';
import MoonAllRecordsTab from './CMS/Moon/MoonAllRecordsTab';

type Tab = 'stats' | 'users' | 'chat' | 'orders' | 'products' | 'horoscope' | 'sky' | 'blog' | 'comments' | 'payments' | 'media' | 'settings' | 'testing';

interface Product {
    id: number;
    name: string;
    slug: string;
    price: number;
    category: string; 
    media: string;
}

interface Order {
    order_number: string;
    customer_email: string;
    total_amount: number;
    status: string;
    created_at: number;
}

interface Horoscope {
    id: number;
    sign: string;
    vertical: string;
    period: string;
    period_start: string;
    period_end: string;
    text: string;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

interface MediaItem {
    id: number;
    url: string;
    url_webp?: string;
    type: number;
    size?: number;
}

interface BlogPost {
  id: number;
  title: string;
  status: string;
  author_id: number;
  created_at: number;
  categories: string | null;
}

const CMS: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const adminPath = pathSegments[0] || '';
  const activeTab = (pathSegments[1] as Tab) || 'stats';
  const activeSubTab = pathSegments[2];

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (activeTab === 'sky' && !(segments[2] === 'moon' && segments[3] === 'all')) {
      navigate(`/${adminPath}/sky/moon/all`, { replace: true });
    }
  }, [activeTab, adminPath, navigate, location.pathname]);

  const tabName = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
  useSeo({ title: `CMS - ${tabName} - esoteric.pl` });
  
  const [usersList, setUsersList] = useState<UserProfile[]>([]);
  const [expertsList, setExpertsList] = useState<Expert[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [ordersList, setOrdersList] = useState<Order[]>([]);
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  
  const [horoscopeData, setHoroscopeData] = useState<{ data: Horoscope[], total: number }>({ data: [], total: 0 });
  const [horoscopePage, setHoroscopePage] = useState(1);
  const [horoscopeLimit, setHoroscopeLimit] = useState<number | 'all'>(50);

  const [moonPhaseData, setMoonPhaseData] = useState<{ data: MoonPhaseRow[], total: number }>({ data: [], total: 0 });
  const [moonPhasePage, setMoonPhasePage] = useState(1);
  const [moonPhaseLimit, setMoonPhaseLimit] = useState<number | 'all'>(50);
  const [moonFilterFrom, setMoonFilterFrom] = useState('');
  const [moonFilterTo, setMoonFilterTo] = useState('');

  const [blogData, setBlogData] = useState<{ data: BlogPost[], total: number }>({ data: [], total: 0 });
  const [blogPage, setBlogPage] = useState(1);
  const [blogSearchQuery, setBlogSearchQuery] = useState('');
  const [blogSlugQuery, setBlogSlugQuery] = useState('');
  const [blogCategoryFilter, setBlogCategoryFilter] = useState<number[]>([]);
  const [blogCreatedFrom, setBlogCreatedFrom] = useState('');
  const [blogCreatedTo, setBlogCreatedTo] = useState('');
  
  const [previewMedia, setPreviewMedia] = useState<MediaItem | null>(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  const [optimizeQuality, setOptimizeQuality] = useState(80);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isGeneratingWebp, setIsGeneratingWebp] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState<number | null>(null);

  // Require entry through secret CMS slug; direct /cms access should show 404
  useEffect(() => {
    try {
      const hasCmsEntry = typeof window !== 'undefined'
        ? window.localStorage.getItem('cms_entry') === 'ok'
        : false;
      if (!hasCmsEntry) {
        navigate('/404', { replace: true });
      }
    } catch {
      navigate('/404', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const verifyAdminAccess = async () => {
        const sessionUser = getSessionUser();
        
        if (!sessionUser || !sessionUser.phone) {
            window.location.href = '/404';
            return;
        }

        setIsLoading(true);
        try {
            const fullUser = await getUserByPhone(sessionUser.phone);

            if (fullUser && fullUser.rank === 0) {
                saveSessionUser(fullUser);
                setCurrentUser(fullUser);
            } else {
                window.location.href = '/404';
                return;
            }
        } catch (error) {
            console.error("Admin verification failed:", error);
            window.location.href = '/404';
            return;
        } finally {
            setIsLoading(false);
        }
    };
    
    verifyAdminAccess();
  }, []);

  useEffect(() => {
      if (currentUser) {
        loadTabData(activeTab, currentUser);
        fetchChatUnread(currentUser);
      }
  }, [
    activeTab,
    activeSubTab,
    currentUser,
    horoscopePage,
    horoscopeLimit,
    moonPhasePage,
    moonPhaseLimit,
    blogPage,
    blogSearchQuery,
    blogSlugQuery,
    blogCategoryFilter,
    blogCreatedFrom,
    blogCreatedTo
  ]);

  const loadTabData = (tab: Tab, admin: UserProfile) => {
    if (tab === 'users') {
        fetchUsers(admin);
        fetchExperts(admin);
    } else if (tab === 'orders') {
        fetchOrders(admin);
    } else if (tab === 'products') {
        fetchProducts(admin);
    } else if (tab === 'horoscope') {
        fetchExperts(admin);
        // Determine the period filter based on the sub-tab for paginated fetching
        const periodFilter = ['yearly', 'monthly', 'weekly', 'daily'].includes(activeSubTab || '') ? activeSubTab : undefined;
        // Dla Auto Updatera korzystamy z osobnego endpointu, więc nie musimy pobierać listy horoskopów.
        if (activeSubTab !== 'autoupdater') {
          fetchHoroscopes(admin, horoscopePage, horoscopeLimit, periodFilter);
        }
    } else if (tab === 'sky') {
        fetchMoonPhases(admin, moonPhasePage, moonPhaseLimit, moonFilterFrom, moonFilterTo);
    } else if (tab === 'blog') {
        fetchBlogPosts(admin, blogPage, blogSearchQuery);
    } else if (tab === 'media') {
        fetchMedia(admin);
    } else if (tab === 'comments') {
        fetchExperts(admin);
    }
  };

  const fetchUsers = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/users', { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) setUsersList(await res.json());
    } catch (e) { console.error("Failed to fetch users", e); }
  };
  
  const fetchExperts = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/cms/experts', { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) setExpertsList(await res.json());
    } catch (e) { console.error("Failed to fetch experts", e); }
  };

  const fetchOrders = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/shop/orders', { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) setOrdersList(await res.json());
    } catch (e) { console.error("Failed to fetch orders", e); }
  };

  const fetchChatUnread = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/chat?admin_unread_total=1', { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) {
            const data = await res.json();
            const n = Number(data?.unreadCount);
            setChatUnreadCount(Number.isFinite(n) ? n : 0);
        }
    } catch (e) {
        console.error('Failed to fetch chat unread total', e);
    }
  };

  // Pobierz liczbę nieprzeczytanych od razu po zalogowaniu (badge w menu)
  useEffect(() => {
    if (!currentUser) return;
    fetchChatUnread(currentUser);
  }, [currentUser]);

  // Na zakładce Chat: od razu odśwież + co 10 s
  useEffect(() => {
    if (!currentUser || activeTab !== 'chat') return;
    fetchChatUnread(currentUser);
    const interval = setInterval(() => fetchChatUnread(currentUser), 10000);
    return () => clearInterval(interval);
  }, [activeTab, currentUser]);

  const fetchProducts = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/shop/products');
        if (res.ok) setProductsList(await res.json());
    } catch (e) { console.error("Failed to fetch products", e); }
  };
  
  const fetchHoroscopes = async (admin: UserProfile, page = 1, limit: number | 'all' = 50, period?: string) => {
    try {
        let url = `/api/cms/horoscope?page=${page}&limit=${limit}`;
        if (period) {
            url += `&period=${period}`;
        }
        const res = await fetch(url, { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) {
            const responseData = await res.json();
            setHoroscopeData({ data: responseData.data, total: responseData.total });
            setHoroscopePage(responseData.page);
        }
    } catch (e) { console.error("Failed to fetch horoscopes", e); }
  };

  const fetchMoonPhases = async (
    admin: UserProfile,
    page = 1,
    limit: number | 'all' = 50,
    dateFrom = '',
    dateTo = ''
  ) => {
    try {
      let url = `/api/cms/moon-phase?page=${page}&limit=${limit}`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const res = await fetch(url, { headers: { 'x-admin-phone': admin.phone } });
      if (res.ok) {
        const responseData = await res.json();
        setMoonPhaseData({ data: responseData.data, total: responseData.total });
        setMoonPhasePage(responseData.page);
      }
    } catch (e) {
      console.error('Failed to fetch moon phases', e);
    }
  };
  
  const handleHoroscopeLimitChange = (newLimit: number | 'all') => {
      setHoroscopeLimit(newLimit);
      setHoroscopePage(1);
  };

  const fetchBlogPosts = async (admin: UserProfile, page = 1) => {
    try {
        let url = `/api/cms/blog?page=${page}&limit=50`;
        if (blogSearchQuery) {
            url += `&title=${encodeURIComponent(blogSearchQuery)}`;
        }
        if (blogSlugQuery) {
            url += `&slug=${encodeURIComponent(blogSlugQuery)}`;
        }
        if (blogCategoryFilter.length > 0) {
            url += `&category_ids=${blogCategoryFilter.join(',')}`;
        }
        if (blogCreatedFrom) {
            url += `&created_from=${blogCreatedFrom}`;
        }
        if (blogCreatedTo) {
            url += `&created_to=${blogCreatedTo}`;
        }

        const res = await fetch(url, { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) {
            const responseData = await res.json();
            if (Array.isArray(responseData)) {
                setBlogData({ data: responseData, total: responseData.length });
            } else {
                setBlogData({ data: responseData.data, total: responseData.total });
                setBlogPage(responseData.page);
            }
        }
    } catch (e) { console.error("Failed to fetch blog posts", e); }
  };

  const handleBlogPageChange = (newPage: number) => {
      if (currentUser) {
          setBlogPage(newPage);
          fetchBlogPosts(currentUser, newPage);
      }
  };

  const handleHoroscopePageChange = (newPage: number) => {
    setHoroscopePage(newPage);
  };

  const handleMoonPhasePageChange = (newPage: number) => {
    setMoonPhasePage(newPage);
  };

  const handleMoonPhaseLimitChange = (newLimit: number | 'all') => {
    setMoonPhaseLimit(newLimit);
    setMoonPhasePage(1);
  };

  const handleMoonApplyFilters = () => {
    setMoonPhasePage(1);
    if (currentUser) {
      fetchMoonPhases(currentUser, 1, moonPhaseLimit, moonFilterFrom, moonFilterTo);
    }
  };

  const fetchMedia = async (admin: UserProfile) => {
    try {
        const res = await fetch('/api/cms/media', { headers: { 'x-admin-phone': admin.phone } });
        if (res.ok) setMediaList(await res.json());
    } catch (e) { console.error("Failed to fetch media", e); }
  };

  const handleGenerateWebp = async () => {
    if (!previewMedia || !currentUser) return;
    setIsGeneratingWebp(true);
    try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const url = new URL(previewMedia.url);
        url.searchParams.set('t', Date.now().toString());
        img.src = url.toString();

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = (err) => reject(new Error(`Failed to load image from ${previewMedia.url}. This is likely a CORS issue. Please check the 'Access-Control-Allow-Origin' header on the asset server.`));
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error("Canvas context failed");
        ctx.drawImage(img, 0, 0);

        const webpDataUrl = canvas.toDataURL('image/webp');
        if (!webpDataUrl || !webpDataUrl.startsWith('data:image/webp')) {
           throw new Error("Client-side WebP conversion failed or was not supported for this image format.");
        }

        const res = await fetch('/api/cms/media', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
            body: JSON.stringify({ id: previewMedia.id, url_webp: webpDataUrl })
        });

        if (res.ok) {
            const updatedMediaItem = await res.json();
            setPreviewMedia(updatedMediaItem);
            fetchMedia(currentUser);
            alert(`WebP version generated successfully!`);
        } else {
            const err = await res.json();
            throw new Error(`WebP generation failed on server: ${err.error}`);
        }
    } catch (e) {
        console.error(e);
        alert(`Error during WebP generation: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
        setIsGeneratingWebp(false);
    }
  };

  const handleOptimizeImage = async () => {
    if (!previewMedia || !currentUser) return;
    setIsOptimizing(true);
    try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const url = new URL(previewMedia.url);
        url.searchParams.set('t', Date.now().toString());
        img.src = url.toString();

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error(`Failed to load image from ${previewMedia.url}. This is a CORS issue.`));
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error("Canvas context failed");
        ctx.drawImage(img, 0, 0);

        const newDataUrl = canvas.toDataURL('image/jpeg', optimizeQuality / 100);

        const res = await fetch('/api/cms/media', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
            body: JSON.stringify({ id: previewMedia.id, url: newDataUrl })
        });

        if (res.ok) {
            const data = await res.json();
            await fetchMedia(currentUser);
            setPreviewMedia(prev => prev ? { ...prev, size: data.size, url: `${data.url}?t=${Date.now()}` } : null);
            alert(`Optimized! New Size: ${(data.size / 1024).toFixed(1)} KB`);
        } else {
             const err = await res.json();
             throw new Error(`Optimization failed on server: ${err.error}`);
        }
    } catch (e) {
        console.error(e);
        alert(`Error during optimization: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
        setIsOptimizing(false);
    }
  };

  const handleCopyPreviewLink = () => {
    if (previewMedia) {
        navigator.clipboard.writeText(previewMedia.url);
        setPreviewCopied(true);
        setTimeout(() => setPreviewCopied(false), 2000);
    }
  };

  const renderActiveTab = () => {
      if (!currentUser) return null;

      switch(activeTab) {
          case 'users': {
              const subTabs = [
                  { id: 'users', label: 'Users', icon: Users },
                  { id: 'experts', label: 'Experts', icon: Briefcase },
                  { id: 'bots', label: 'Bots', icon: Bot },
              ];
              const currentSubTab = activeSubTab || 'users';

              const renderContent = () => {
                  switch(currentSubTab) {
                      case 'experts':
                          return <ExpertsTab currentUser={currentUser} expertsList={expertsList} onDataNeededRefresh={() => fetchExperts(currentUser)} />;
                      case 'bots':
                          return <UsersTab currentUser={currentUser} usersList={usersList} rankFilter={[3]} onDataNeededRefresh={() => fetchUsers(currentUser)} />;
                      case 'users':
                      default:
                          return <UsersTab currentUser={currentUser} usersList={usersList} rankFilter={[0, 1]} onDataNeededRefresh={() => fetchUsers(currentUser)} />;
                  }
              };

              return (
                  <div>
                      <div className="bg-mystic-box border-b border-gray-800">
                          <nav className="flex items-center gap-2 p-2 px-6">
                              {subTabs.map(tab => (
                                  <button
                                      key={tab.id}
                                      onClick={() => {
                                          const path = `/${adminPath}/users${tab.id === 'users' ? '' : '/' + tab.id}`;
                                          navigate(path);
                                      }}
                                      className={`flex items-center gap-2 px-4 py-1.5 text-sm rounded transition-colors ${currentSubTab === tab.id ? 'bg-mystic-accent text-black font-bold' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
                                  >
                                      <tab.icon className="w-4 h-4" />
                                      {tab.label}
                                  </button>
                              ))}
                          </nav>
                      </div>
                      <div className="p-6">
                        {renderContent()}
                      </div>
                  </div>
              );
          }
          case 'chat':
              return <CmsChatPage currentUser={currentUser} />;
          case 'orders':
              return <OrdersTab ordersList={ordersList} />;
          case 'products':
              return <ProductsTab currentUser={currentUser} productsList={productsList} onDataNeededRefresh={() => fetchProducts(currentUser)} />;
          case 'horoscope': {
              const subTabs = [
                  { id: 'all', label: 'All Records' },
                  { id: 'daily', label: 'Daily' },
                  { id: 'weekly', label: 'Weekly' },
                  { id: 'monthly', label: 'Monthly' },
                  { id: 'yearly', label: 'Yearly' },
                  { id: 'autoupdater', label: 'Auto Updater' },
              ];
              const currentSubTab = activeSubTab || 'all';
          
              const renderContent = () => {
                  switch(currentSubTab) {
                      case 'autoupdater':
                          return (
                              <AutoUpdaterTab
                                  currentUser={currentUser}
                              />
                          );
                      case 'yearly':
                          return <YearlyTab 
                                    horoscopeList={horoscopeData.data} 
                                    currentUser={currentUser} 
                                    experts={expertsList}
                                    onDataNeededRefresh={() => loadTabData('horoscope', currentUser)}
                                 />;
                      case 'monthly':
                          return <MonthlyTab 
                                    horoscopeList={horoscopeData.data} 
                                    currentUser={currentUser} 
                                    experts={expertsList}
                                    onDataNeededRefresh={() => loadTabData('horoscope', currentUser)}
                                 />;
                       case 'weekly':
                          return <WeeklyTab 
                                    horoscopeList={horoscopeData.data} 
                                    currentUser={currentUser} 
                                    experts={expertsList}
                                    onDataNeededRefresh={() => loadTabData('horoscope', currentUser)}
                                 />;
                        case 'daily':
                          return <DailyTab 
                                    horoscopeList={horoscopeData.data}
                                    totalCount={horoscopeData.total}
                                    currentPage={horoscopePage}
                                    onPageChange={handleHoroscopePageChange}
                                    limit={horoscopeLimit}
                                    onLimitChange={handleHoroscopeLimitChange}
                                    currentUser={currentUser} 
                                    experts={expertsList}
                                    onDataNeededRefresh={() => loadTabData('horoscope', currentUser)}
                                 />;
                      case 'all':
                      default:
                          return <HoroscopeTab 
                                    currentUser={currentUser} 
                                    horoscopeList={horoscopeData.data} 
                                    totalCount={horoscopeData.total}
                                    currentPage={horoscopePage}
                                    onPageChange={handleHoroscopePageChange}
                                    limit={horoscopeLimit}
                                    onLimitChange={handleHoroscopeLimitChange}
                                    onDataNeededRefresh={() => fetchHoroscopes(currentUser, horoscopePage, horoscopeLimit)} 
                                />;
                  }
              };
          
              return (
                  <div>
                      <div className="bg-mystic-box border-b border-gray-800">
                          <nav className="flex items-center justify-between p-2 px-6 gap-4">
                              <div className="flex items-center gap-2">
                                  {subTabs
                                    .filter(tab => tab.id !== 'autoupdater')
                                    .map(tab => (
                                      <button
                                          key={tab.id}
                                          onClick={() => {
                                              const path = `/${adminPath}/horoscope${tab.id === 'all' ? '' : '/' + tab.id}`;
                                              navigate(path);
                                          }}
                                          className={`px-4 py-1.5 text-sm rounded transition-colors ${
                                            currentSubTab === tab.id
                                              ? 'bg-mystic-accent text-black font-bold'
                                              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                          }`}
                                      >
                                          {tab.label}
                                      </button>
                                  ))}
                              </div>
                              <button
                                  onClick={() => {
                                      const path = `/${adminPath}/horoscope/autoupdater`;
                                      navigate(path);
                                  }}
                                  className={`px-4 py-1.5 text-sm rounded transition-colors ${
                                    currentSubTab === 'autoupdater'
                                      ? 'bg-mystic-accent text-black font-bold'
                                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                  }`}
                              >
                                  Auto Updater
                              </button>
                          </nav>
                      </div>
                      <div className="p-6">
                        {renderContent()}
                      </div>
                  </div>
              );
          }
          case 'sky': {
              const skySub = activeSubTab || 'moon';
              const moonSub = pathSegments[3] || 'all';
              const skyMoonActive = skySub === 'moon' && moonSub === 'all';
              return (
                  <div>
                      <div className="bg-mystic-box border-b border-gray-800">
                          <nav className="flex items-center gap-2 p-2 px-6">
                              <button
                                  type="button"
                                  onClick={() => navigate(`/${adminPath}/sky/moon/all`)}
                                  className={`px-4 py-1.5 text-sm rounded transition-colors ${
                                    skySub === 'moon'
                                      ? 'bg-mystic-accent text-black font-bold'
                                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                  }`}
                              >
                                  MOON
                              </button>
                          </nav>
                      </div>
                      <div className="bg-mystic-box border-b border-gray-800">
                          <nav className="flex items-center gap-2 p-2 px-6">
                              <button
                                  type="button"
                                  onClick={() => navigate(`/${adminPath}/sky/moon/all`)}
                                  className={`px-4 py-1.5 text-sm rounded transition-colors ${
                                    skyMoonActive
                                      ? 'bg-mystic-accent text-black font-bold'
                                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                  }`}
                              >
                                  All records
                              </button>
                          </nav>
                      </div>
                      <div className="p-6">
                          {skyMoonActive && currentUser ? (
                              <MoonAllRecordsTab
                                  currentUser={currentUser}
                                  rows={moonPhaseData.data}
                                  totalCount={moonPhaseData.total}
                                  currentPage={moonPhasePage}
                                  onPageChange={handleMoonPhasePageChange}
                                  limit={moonPhaseLimit}
                                  onLimitChange={handleMoonPhaseLimitChange}
                                  filterFrom={moonFilterFrom}
                                  filterTo={moonFilterTo}
                                  onFilterFromChange={setMoonFilterFrom}
                                  onFilterToChange={setMoonFilterTo}
                                  onApplyFilters={handleMoonApplyFilters}
                                  onDataRefresh={() => {
                                      if (currentUser) {
                                          fetchMoonPhases(
                                              currentUser,
                                              moonPhasePage,
                                              moonPhaseLimit,
                                              moonFilterFrom,
                                              moonFilterTo
                                          );
                                      }
                                  }}
                              />
                          ) : null}
                      </div>
                  </div>
              );
          }
        case 'blog':
            return <BlogTab 
                currentUser={currentUser} 
                blogPosts={blogData.data} 
                totalCount={blogData.total}
                currentPage={blogPage}
                onPageChange={handleBlogPageChange}
                onDataNeededRefresh={() => fetchBlogPosts(currentUser, blogPage)} 
                titleQuery={blogSearchQuery}
                onTitleQueryChange={setBlogSearchQuery}
                slugQuery={blogSlugQuery}
                onSlugQueryChange={setBlogSlugQuery}
                selectedCategoryIds={blogCategoryFilter}
                onSelectedCategoryIdsChange={setBlogCategoryFilter}
                createdFrom={blogCreatedFrom}
                createdTo={blogCreatedTo}
                onCreatedFromChange={setBlogCreatedFrom}
                onCreatedToChange={setBlogCreatedTo}
            />;
          case 'comments':
              return <div className="p-6"><CommentsManager currentUser={currentUser} experts={expertsList} /></div>;
          case 'media':
              return <MediaTab currentUser={currentUser} mediaList={mediaList} onDataNeededRefresh={() => fetchMedia(currentUser)} setPreviewMedia={setPreviewMedia} />;
          case 'payments':
              return <PaymentsTab />;
          case 'testing':
              return <TestingTab currentUser={currentUser} />;
          case 'stats':
              return <StatsTab />;
          case 'settings':
              return <SettingsPage activeSubTab={activeSubTab} />;
          default:
              return <StatsTab />;
      }
  };

  if (isLoading) return <div className="min-h-screen bg-mystic-bg flex items-center justify-center"><Loader2 className="animate-spin text-white" /></div>;

  return (
    <div className="h-screen overflow-hidden bg-mystic-bg text-[#e3e3e3] font-mono text-sm flex flex-col">
      <header className="flex-none border-b border-gray-800 bg-mystic-box px-6 h-14 flex items-center justify-between z-50">
        <div className="flex items-center gap-6">
            <span className="font-bold text-mystic-accent tracking-wider">CMS v1.0</span>
            <nav className="flex gap-1">
                <button onClick={() => navigate(`/${adminPath}/stats`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'stats' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Stats</button>
                <button onClick={() => navigate(`/${adminPath}/users`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'users' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Users</button>
                <button onClick={() => navigate(`/${adminPath}/chat`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'chat' ? 'text-white bg-white/10' : 'text-gray-500'} flex items-center gap-1`}>
                    <span>Chat</span>
                    {Number(chatUnreadCount) > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                            {Math.min(Number(chatUnreadCount), 99)}
                        </span>
                    )}
                </button>
                <button onClick={() => navigate(`/${adminPath}/orders`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'orders' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Orders</button>
                <button onClick={() => navigate(`/${adminPath}/products`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'products' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Products</button>
                <button onClick={() => navigate(`/${adminPath}/horoscope`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'horoscope' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Horoscope</button>
                <button onClick={() => navigate(`/${adminPath}/sky/moon/all`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'sky' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Sky</button>
                <button onClick={() => navigate(`/${adminPath}/blog`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'blog' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Blog</button>
                <button onClick={() => navigate(`/${adminPath}/comments`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'comments' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Comments</button>
                <button onClick={() => navigate(`/${adminPath}/payments`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'payments' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Payments</button>
                <button onClick={() => navigate(`/${adminPath}/media`)} className={`px-3 py-1.5 rounded hover:bg-white/5 ${activeTab === 'media' ? 'text-white bg-white/10' : 'text-gray-500'}`}>Media</button>
            </nav>
        </div>
        <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/${adminPath}/testing`)} className={`p-2 rounded flex items-center justify-center hover:bg-white/5 ${activeTab === 'testing' ? 'text-white bg-white/10' : 'text-gray-500'}`} aria-label="Testing">
                <TestTube className="w-5 h-5" />
            </button>
             <button onClick={() => navigate(`/${adminPath}/settings`)} className={`p-2 rounded flex items-center justify-center hover:bg-white/5 ${activeTab === 'settings' ? 'text-white bg-white/10' : 'text-gray-500'}`} aria-label="Settings">
                <Settings className="w-5 h-5" />
            </button>
        </div>
      </header>

      <main className={`flex-1 overflow-y-auto ${['settings', 'horoscope', 'sky', 'users', 'comments', 'chat'].includes(activeTab) ? '' : 'p-6'}`}>
        {renderActiveTab()}
      </main>

      {previewMedia && (
          <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-mystic-box border border-gray-700 w-full max-w-5xl max-h-[95vh] rounded-xl flex flex-col md:flex-row shadow-2xl relative overflow-hidden">
                  <button 
                      onClick={() => setPreviewMedia(null)}
                      className="absolute top-4 right-4 p-2 bg-black/50 text-white hover:bg-red-500 hover:text-white rounded-full transition-colors z-20"
                  >
                      <X className="w-5 h-5" />
                  </button>
                  <div className="flex-1 bg-black/20 flex items-center justify-center p-8 overflow-hidden relative min-h-[50vh]">
                      <img 
                          src={previewMedia.url} 
                          alt="Preview" 
                          className="max-w-full max-h-full object-contain shadow-2xl" 
                      />
                  </div>
                  <div className="w-full md:w-80 bg-mystic-bg border-l border-gray-700 flex flex-col">
                      <div className="p-6 border-b border-gray-800">
                          <h3 className="text-white font-bold mb-1">Media Details</h3>
                          <div className="text-xs text-gray-500 font-mono break-all">{previewMedia.url}</div>
                           {previewMedia.url_webp ? (
                              <div className="text-xs text-mystic-accent font-mono break-all mt-2">{previewMedia.url_webp}</div>
                          ) : (
                              <button
                                  onClick={handleGenerateWebp}
                                  disabled={isGeneratingWebp}
                                  className="w-full text-left mt-3 bg-black/20 hover:bg-black/40 text-mystic-accent py-2 px-3 rounded font-bold text-xs transition-colors flex items-center gap-2 disabled:opacity-50"
                              >
                                  {isGeneratingWebp ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                  {isGeneratingWebp ? 'Generating...' : 'Generate .webp version'}
                              </button>
                          )}
                      </div>
                      <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                          <div>
                              <label className="text-xs uppercase text-gray-500 font-bold mb-2 block">Original Size</label>
                              <div className="text-white font-mono text-lg">
                                  {((previewMedia.size || 0) / 1024).toFixed(2)} KB
                              </div>
                          </div>
                          <div className="p-4 bg-black/20 rounded-xl border border-gray-800">
                                <div className="flex items-center gap-2 text-mystic-accent mb-4">
                                    <Sliders className="w-4 h-4" />
                                    <span className="text-sm font-bold uppercase">Optimization</span>
                                </div>
                                <label className="block text-xs text-gray-400 mb-2">Quality: {optimizeQuality}%</label>
                                <input 
                                    type="range" 
                                    min="10" 
                                    max="100" 
                                    value={optimizeQuality}
                                    onChange={(e) => setOptimizeQuality(parseInt(e.target.value))}
                                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-mystic-accent mb-4"
                                />
                                <button 
                                    onClick={handleOptimizeImage}
                                    disabled={isOptimizing}
                                    className="w-full bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white py-2 rounded font-bold text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isOptimizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
                                    {isOptimizing ? 'Compressing...' : 'Optimize & Overwrite'}
                                </button>
                                <p className="text-[10px] text-gray-600 mt-2 text-center leading-tight">
                                    Converts to JPEG and overwrites existing file.
                                </p>
                          </div>
                      </div>
                      <div className="p-6 border-t border-gray-800 bg-mystic-box">
                        <button 
                            onClick={handleCopyPreviewLink}
                            className="w-full flex items-center justify-center gap-2 bg-mystic-accent hover:bg-mystic-accentHover text-black px-6 py-3 rounded font-bold transition-all shadow-lg active:scale-95"
                        >
                            {previewCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {previewCopied ? 'Link Copied' : 'Copy Link'}
                        </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default CMS;